/**
 * App-lock preference: whether the biometric session gate arms on app open and
 * re-arms on resume. Opt-in and OFF by default — re-locking interrupts whatever
 * the user was doing (half-filled forms, an open send-money flow), so it stays a
 * deliberate per-device choice made in Profile → Security.
 *
 * Independent of the high-value transaction biometric confirm, which is always
 * on and does not disturb in-progress work.
 */
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'artham.appLockEnabled';

const listeners = new Set<() => void>();
let cached: boolean | null = null;

/** Reads the persisted preference. Absent/unparsed means off. */
export function isAppLockEnabled(): boolean {
  if (cached === null) {
    if (typeof window === 'undefined') return false;
    cached = window.localStorage.getItem(STORAGE_KEY) === 'true';
  }
  return cached;
}

export function setAppLockEnabled(enabled: boolean): void {
  cached = enabled;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, String(enabled));
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useAppLockEnabled(): boolean {
  return useSyncExternalStore(subscribe, isAppLockEnabled, () => false);
}
